import axios, { type AxiosInstance } from "axios";

/**
 * The admin portal's HTTP client.
 *
 * Deliberately simpler than the merchant client (apps/web lib/api.ts):
 *
 *  - NO X-Tenant-Id. The admin realm is cross-org; every endpoint names the org in its
 *    path, never in a header.
 *  - NO refresh. The admin realm has no refresh token (admin.routes.ts) — staff sign in
 *    again each hour, a deliberate trade for a session that reaches every organization. So
 *    a 401 means exactly one thing: the session is over, show the login screen.
 *  - Cross-origin with credentials. admin.ecomally.in calls api.ecomally.in, so
 *    `withCredentials` sends the admin session cookie and the CORS allowlist lets it back.
 *
 * The API URL is fetched from /config.json at boot, not baked in — one image promoted by
 * digest across dev → uat → prod (doc 06 §5), same as the merchant app.
 */
export interface RuntimeConfig {
  readonly apiUrl: string;
  readonly appEnv: string;
}

export async function loadRuntimeConfig(fetchImpl: typeof fetch = fetch): Promise<RuntimeConfig> {
  const response = await fetchImpl("/config.json", { cache: "no-store" });
  if (!response.ok) throw new Error(`Could not load /config.json (${String(response.status)})`);

  const body: unknown = await response.json();
  if (
    typeof body !== "object" ||
    body === null ||
    typeof (body as { apiUrl?: unknown }).apiUrl !== "string" ||
    (body as { apiUrl: string }).apiUrl === ""
  ) {
    throw new Error("/config.json is missing a usable apiUrl");
  }
  const parsed = body as { apiUrl: string; appEnv?: unknown };
  return { apiUrl: parsed.apiUrl, appEnv: typeof parsed.appEnv === "string" ? parsed.appEnv : "unknown" };
}

export interface AdminClientOptions {
  readonly baseURL: string;
  /** Called on 401 — the admin session is gone and the portal must return to sign-in. */
  readonly onUnauthenticated: () => void;
}

export function createAdminClient(options: AdminClientOptions): AxiosInstance {
  const client = axios.create({
    baseURL: options.baseURL,
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
  });

  client.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      /**
       * `axios.isAxiosError`, not `instanceof` — the prototype check fails across a
       * duplicated axios copy in the bundle, and when it fails the interceptor silently
       * stops handling 401s while every request still "works". The flag check has no such
       * failure mode.
       */
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      const url = axios.isAxiosError(error) ? (error.config?.url ?? "") : "";
      // The login POST 401s on a bad password — that is the form's own error to show, not
      // a reason to declare the (nonexistent) session over.
      if (status === 401 && url !== "/admin/auth/login") {
        options.onUnauthenticated();
      }
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    },
  );

  return client;
}

/** The message the API sent, or a fallback — narrowed, never stringified to "[object Object]". */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (!axios.isAxiosError(error)) return fallback;
  const data: unknown = error.response?.data;
  if (typeof data !== "object" || data === null) return fallback;
  const message: unknown = (data as { message?: unknown }).message;
  return typeof message === "string" && message !== "" ? message : fallback;
}
